import { Flex } from "@chakra-ui/react";
import Image from "next/legacy/image";
import React from "react";
import MetaTags from "../components/MetaTags";
import styles from "../styles/body.module.scss";

// --------------------------------------------------------
// Data
// --------------------------------------------------------

export const getStaticProps = async () => {
  return {
    props: {}
  };
};

// --------------------------------------------------------
// Components
// --------------------------------------------------------

const TermsPage = () => {
  return <>
            <MetaTags title="Terms and Conditions" description="Review LiveLink AI’s terms of service to understand your rights and responsibilities while using our platform, ensuring a safe and enjoyable experience." data-sentry-element="MetaTags" data-sentry-source-file="terms.tsx" />
            <div className={styles.Body}>
                <Flex justifyContent="center" alignItems="center" py="25px" data-sentry-element="Flex" data-sentry-source-file="terms.tsx">
                    <Image src="/images/livelink_logo_text.svg" alt="LiveLink Logo" width={150} height={150} data-sentry-element="Image" data-sentry-source-file="terms.tsx" />
                </Flex>
                <h1>Terms and conditions</h1>
                <h2>Introduction</h2>
                <p>
                    Welcome to LiveLink. This &apos;App&apos; (henceforth refers the LiveLink&apos;s
                    iOS platform, android platform and web platform) is published by or on behalf of
                    Scoodle Ltd; a company registered in England and Wales under company number
                    10306849 whose registered office at 27 Ash Court, London, United Kingdom, N11
                    2NG.
                </p>
                <p>
                    By downloading or otherwise accessing the App, you agree to be bound by the
                    following terms and conditions (“Terms“) and our privacy policy. If you have any
                    queries about the App or these Terms, you can contact us by any of the means set
                    out in the contact us section of these terms. If you do not agree with these
                    terms, you must stop using the App immediately.
                </p>
                <h2>General</h2>
                <p>
                    The App is made available for your personal use. The App must not be used for
                    any commercial purpose or for any illegal or unauthorised purpose. When you use
                    the App you must comply with all applicable UK laws and with any applicable
                    international laws, including the local laws in your country of residence
                    (together referred to as “Applicable Laws”).
                </p>
                <p>
                    You agree that when using the App you will comply with all Applicable Laws and
                    these Terms. In particular, but not limited to, you agree not to:
                </p>
                <p>
                    (a) Use the App in any unlawful manner or in a manner which promotes or
                    encourages illegal activity including (without limitation) copyright
                    infringement; or
                </p>
                <p>
                    (b) Attempt to gain unauthorised access to the App or any networks, servers or
                    computer systems connected to the App; or
                </p>
                <p>
                    (c) Modify, adapt, translate or reverse engineer any part of the App or
                    re-format or frame any portion of the pages comprising the App, save to the
                    extent expressly permitted by these Terms or by law.
                </p>
                <h2>Competitions</h2>
                <p>
                    If you take part in any competition which is run in or through the App
                    (“Competition“), you agree to be bound by the rules of that competition and any
                    other rules specified by LiveLink from time to time (“Competition Rules“) and by
                    the decisions of LiveLink, which are final in all matters relating to the
                    Competition. LiveLink reserves the right to disqualify any entrant and/or winner
                    in its absolute discretion without notice in accordance with the Competition
                    Rules.
                </p>
                <h2>Content</h2>
                <p>
                    The copyright in all material contained on, in, or available through the App
                    including all information, data, text, music, sound, photographs, graphics and
                    video messages, the selection and arrangement thereof, and all source code,
                    software compilations and other material (“Material“) is owned by or licensed to
                    LiveLink or its group companies. All rights are reserved. You cannot otherwise
                    copy, edit, vary, reproduce, publish, display, distribute, store, transmit,
                    commercially exploit, disseminate in any form whatsoever or use the Material
                    without LiveLink&apos;s express permission.
                </p>
                <p>
                    The trademarks, service marks, and logos (“Trade Marks“) contained on or in the
                    App are owned by LiveLink or its group companies or third party partners of
                    LiveLink. You cannot use, copy, edit, vary, reproduce, publish, display,
                    distribute, store, transmit, commercially exploit or disseminate the Trade Marks
                    without the prior written consent of LiveLink or the relevant group company or
                    the relevant third party partner of LiveLink.
                </p>
                <h2>Payment Processing</h2>
                <p>
                    Payment processing services for users on LiveLink are provided by Stripe and are
                    subject to the{" "}
                    <a href="https://stripe.com/connect-account/legal">
                        Stripe Connected Account Agreement
                    </a>
                    , which includes the{" "}
                    <a href="https://stripe.com/legal">Stripe Terms of Service</a> (collectively,
                    the “Stripe Services Agreement”). By agreeing to these terms or continuing to
                    operate as a user on LiveLink, you agree to be bound by the Stripe Services
                    Agreement, as the same may be modified by Stripe from time to time. As a
                    condition of LiveLink enabling payment processing services through Stripe, you
                    agree to provide LiveLink accurate and complete information about you, and you
                    authorize LiveLink to share it and transaction information related to your use
                    of the payment processing services provided by Stripe.
                </p>
                <h2>LiveLink&apos;s Rights &amp; Responsibilities</h2>
                <ol>
                    <li>
                        To maintain a functioning App, including communication systems, a booking
                        platform, online video rooms and payment processing, wherever possible.
                    </li>

                    <li>To communicate any planned maintenance of the App or downtime.</li>

                    <li>To reasonably endeavour to keep the site free from viruses and worms.</li>

                    <li>
                        To process refunds promptly in the event that lessons do not take place or a
                        complaint is upheld.
                    </li>

                    <li>
                        To take precautions we consider reasonable to protect user information.
                        LiveLink cannot be held liable for unauthorised access to information by
                        users.
                    </li>

                    <li>
                        To monitor postings made on the App and messages sent between users. We do
                        so in the hope of helping Users and Creators meet, but also to stop misuse
                        of the site, as set out in these terms and conditions. Interaction between
                        users can be shared with relevant third parties to ensure the safety of
                        Users and Creators that use LiveLink.
                    </li>
                </ol>
                <h2>Creator Responsibilities</h2>
                <p>
                    As defined at the end of this document, a “User“ means a person who uses our
                    platform (whether or not registered with us).
                </p>
                <ol>
                    <li>
                        Users are responsible for their own security in conjunction with our
                        services, both online and offline.
                    </li>

                    <li>
                        Users are solely responsible for the material they post on the App,
                        including messages sent, and must not post defamatory, offensive or illegal
                        material.
                    </li>

                    <li>
                        Users must immediately report any defamatory, offensive or illegal material
                        they view on our App.
                    </li>

                    <li>
                        Users must exercise their own judgement regarding the accuracy of
                        information provided on the App. LiveLink cannot guarantee that all of the
                        content on the App is complete, accurate or up-to-date.
                    </li>

                    <li>
                        Users are responsible for their own internet security when using the App.
                    </li>

                    <li>
                        Users must contact LiveLink immediately if they believe their password has
                        been compromised. Users will be responsible for the actions of any
                        interactions conducted in their name until they have notified LiveLink.
                    </li>

                    <li>
                        Users must not use the site with the intention of disintermediating LiveLink
                        in any way. Users must not use the website with the intention of
                        disintermediating LiveLink in any way. Users must not promote opportunities
                        or services of any company other than LiveLink. This includes, but is not
                        limited to, sharing of email addresses, phone numbers and current address.
                    </li>

                    <li>Users agree to abide by the Privacy Policy provided by LiveLink.</li>

                    <li>
                        Any User who fails to meet these terms and conditions may be barred from
                        using the App and related services.
                    </li>
                </ol>
                <h2>Disclaimers and Limitation of Liability</h2>
                <p>
                    Nothing in this agreement in any way limits or excludes our liability for
                    negligence causing death or personal injury or for anything which may not
                    legally be excluded or limited.
                </p>
                <ol>
                    <li>
                        You must give us a reasonable opportunity to remedy any matter for which we
                        are potentially liable before you incur any costs remedying the matter
                        yourself.
                    </li>

                    <li>
                        If you are a User, we shall not be liable for any loss or damage caused by
                        us or our employees or agents in circumstances where:
                        <ul>
                            <li>
                                there is no breach of a legal duty of care owed to you by us or by
                                any of our employees or agents;
                            </li>
                        </ul>
                        <ul>
                            <li>
                                such loss or damage was not reasonably foreseeable by both parties;
                            </li>
                        </ul>
                        <ul>
                            <li>
                                such loss or damage is caused by you, for example by not complying
                                with this agreement;
                            </li>
                        </ul>
                    </li>
                </ol>
                <p>
                    If you are a User, you will be liable for any reasonably foreseeable loss or
                    damage we suffer arising from your breach of this agreement or misuse of our App
                    (subject of course to our obligation to mitigate any losses).
                </p>
                <p>The following clauses apply only if you are not a Consumer:</p>
                <ul>
                    <li>
                        To the extent allowed by law, you and we exclude all terms, whether imposed
                        by statute or by law or otherwise, that are not expressly stated in this
                        agreement. In this clause, any reference to us includes our employees and
                        agents.
                    </li>
                </ul>
                <ul>
                    <li>
                        Our liability of any kind (including our own negligence) with respect to our
                        App for any one event or series of related events is limited to £100 or the
                        total fees payable by you in the 12 months before the event(s) complained
                        of, whichever is higher.
                    </li>
                </ul>
                <ul>
                    <li>
                        In no event (including our own negligence) will we be liable for any:
                        <ul>
                            <li>
                                economic losses (including, without limit, loss of revenues,
                                profits, contracts, business or anticipated savings);
                            </li>
                        </ul>
                        <ul>
                            <li>loss of goodwill or reputation;</li>
                        </ul>
                        <ul>
                            <li>special, indirect or consequential losses; or</li>
                        </ul>
                        <ul>
                            <li>
                                damage to or loss of data (even if we have been advised of the
                                possibility of such losses).
                            </li>
                        </ul>
                    </li>
                </ul>
                <ul>
                    <li>
                        You will indemnify us against all claims and liabilities directly or
                        indirectly related to your use of the App and/or breach of this agreement.
                    </li>
                </ul>
                <ul>
                    <li>
                        This agreement constitutes the entire agreement between us with respect to
                        its subject matter and supercedes any previous communications or agreements
                        between us. We both acknowledge that there have been no misrepresentations
                        and that neither of us has relied on any pre-contractual statements.
                        Liability for misrepresentation (excluding fraudulent misrepresentation)
                        relating to the terms of this agreement is excluded.
                    </li>
                </ul>
                <h2>Changes to the terms and conditions</h2>
                <p>
                    You will be bound by the revised agreement, if you continue to use our App or
                    the Services following the effective date shown.
                </p>
                <h2>Governing Law &amp; Jurisdiction</h2>
                <ol>
                    <li>
                        These terms and conditions shall be governed by English law and any disputes
                        will be decided only by the courts of the United Kingdom.
                    </li>

                    <li>
                        If any clause or part of these terms and conditions are found to be
                        unenforceable in law, the other terms and conditions will remain in force.
                    </li>
                </ol>
                <h2>User Content</h2>
                <p>
                    We do not claim any ownership rights in your User Content. After uploading your
                    User Content to this Website, you continue to retain all ownership rights in
                    such User Content and you continue to have the right to use your User Content in
                    any way you choose.
                </p>
                <p>
                    By uploading any User Content to or on this Website, you grant us a perpetual,
                    royalty free, worldwide, sub-licensable, non-exclusive, irrevocable licence to
                    use, reproduce, distribute, perform, display or electronically transmit the User
                    Content, whether in its original form or as adapted by us, on this Website and
                    to our associated companies, advertising networks, distribution partners,
                    affiliates and third party service providers.
                </p>
                <p>
                    You agree to waive any moral rights you have in User Content (including the
                    right to be identified as the author) so that we may adapt the User Content
                    freely and without restriction.
                </p>
                <p>
                    We may display advertisements relating to or in connection with your User
                    Content and use your User Content for advertising and promotional purposes. You
                    acknowledge and agree that your User Content may be included on websites and
                    advertising networks, distribution partners, affiliates and third party service
                    providers.
                </p>
                <p>
                    Creators should only upload videos that they have made or that they&apos;re
                    authorised to use. That means that they should not upload videos that they
                    didn&apos;t make, or use content in their videos that someone else owns the
                    copyright to.
                </p>
                <p>
                    The information provided can be used for your personal and individual use only,
                    and the material cannot be distributed through any means.
                </p>
                <h3>Your User-Uploaded Content</h3>
                <p>
                    You represent and warrant that: (i) you have the right to upload Content to
                    LiveLink Resources and grant the rights set out in the licences herein (ii)
                    either you own the intellectual property rights in and to the User-Uploaded
                    Content uploaded by you or you are acting as an agent for those who do (and
                    whosoever downloads your content adheres to any licences referenced herein, will
                    not infringe the rights of any third parties) and (iii) any User-Uploaded
                    Content you upload to LiveLink Resources is compliant with both these Additional
                    Terms and the General Terms.
                </p>
                <p>
                    Please note that User-Uploaded Content should not contain any content which has
                    been copied, in whole or in part, from third party materials without the consent
                    of the third party owner. Any such use of third party materials may amount to
                    copyright infringement.
                </p>
                <p>
                    In the event that you remove your User-Uploaded Content from LiveLink, from the
                    date of the removal, the licence(s) granted by you in these Additional Terms
                    will be deemed to have been terminated (save as otherwise provided herein).
                </p>
                <p>
                    Please note that people who have downloaded or otherwise made use of your
                    User-Uploaded Content prior to its removal, or your re-license under an
                    alternative licence, will continue to have those rights that you granted to them
                    at the time of their purchase and/or download of the relevant User-Uploaded
                    Content. Any deletion or re-license shall simply remove LiveLink Education
                    Resources right to make further sales of said materials.
                </p>
                <h3>Infringing User-Uploaded Content</h3>
                <p>
                    In the event that we discover that User-Uploaded Content in use by you infringes
                    the rights of any third party and notify you of this, you are required to, and
                    undertake that you shall, immediately cease all use of the infringing Content,
                    promptly delete and/or destroy any copies of such infringing Content and procure
                    the deletion and/or destruction of any copies of it that you have made available
                    to others.
                </p>
                <h2>Content Storage</h2>
                <p>
                    We reserve the right to establish general practices and limitations regarding
                    the storage of content on this Website, including the maximum number of days
                    that messages, in-mails, postings or other content will be retained by us on the
                    Platform, the maximum number of messages or in-mails that may be sent by or
                    received by an account and the maximum size of messages, in-mails, postings or
                    other contents.
                </p>
                <p>
                    We shall not be held responsible or liable for the deletion or failure to store
                    any messages, communications or other content maintained or transmitted by or
                    under Your Account or any other part of this Website.
                </p>
                <h2>Subscriptions and fees</h2>
                <p>
                    We reserve the right to change or withdraw features, specifications, services
                    and content of a Service Plan at any time, without notice to you. Please note
                    that the content and features of a Service Plan may vary based on when you
                    signed up and whether you have signed up through our website or via the App. The
                    LiveLink Services offered via the App may only contain a limited version of the
                    web based services.
                </p>
                <p>
                    Some Service Plans require payment before you can access them and certain areas
                    of use are only permitted if you have signed up for a Paid Subscription or
                    entered into a separate agreement with LiveLink. If you have signed up for Paid
                    Subscriptions, you may get access to features that are not available through
                    LiveLink Basic and you may use the Services for commercial purposes, only if and
                    to the extent such commercial use is permitted under the selected Service Plan.
                </p>
                <p>
                    We may, at our own sole discretion, offer trials of Paid Subscriptions for a
                    specified period without payment or at a discounted rate (a “Trial”). We reserve
                    the right to revoke the Trial and put your account on hold in the event that we
                    determine that you are not eligible. For some Trials, we&apos;ll require you to
                    provide your payment details to start the Trial. We will charge you on a
                    recurring basis for the selected subscription fee at the end of the Trial using
                    the payment details you have provided us, unless you cancel your subscription
                    prior to the end of the Trial.
                </p>
                <p>
                    You have provided your payment details in conjunction with the Trial and you
                    accept that we charge you using such payment details. If you do not want this
                    charge, you must cancel your Paid Subscriptions by contacting LiveLink before
                    the end of the Trial. If you do not want to continue to be charged on a
                    recurring weekly, monthly or annual basis (as the case may be, depending on your
                    Service Plan), you must cancel the applicable paid subscription by contacting
                    LiveLink before the end of the recurring weekly, monthly or yearly period. Paid
                    Subscriptions cannot be terminated before the end of the period for which you
                    have already paid, and except as expressly provided in these terms, LiveLink
                    will not refund any fees that you have already paid.
                </p>
                <p>
                    If you cancel your Paid Subscription, the cancellation will take effect on the
                    day after the last day of the current subscription period, after which you will
                    be downgraded to LiveLink Basic. We will not refund any subscription fees
                    already paid to us.
                </p>
                <p>
                    LiveLink may change the price for the Paid Subscriptions from time to time, and
                    will communicate any price changes to you in advance and, if applicable, how to
                    accept those changes. Price changes for Paid Subscriptions will take effect at
                    the start of the next subscription period following the date of the price
                    change. As permitted by local law, you accept the new price by continuing to use
                    the LiveLink Service after the price change takes effect. If you do not agree
                    with the price changes, you have the right to reject the change by unsubscribing
                    from the Paid Subscription prior to the price change going into effect, in which
                    case you will be downgraded to LiveLink Basic.
                </p>
                <p>
                    We do not provide any refunds if the price for a Paid Subscription drops, or if
                    we offer subsequent promotional pricing or change the content or features of a
                    Service Plan. Paid Subscriptions purchased through other platforms are subject
                    to the refund policies of those platforms. LiveLink cannot be held responsible
                    for these platforms&apos; policies.
                </p>
                <p>
                    If a user starts a subscription using in app purchases, they can manage them
                    directly on their device. LiveLink does not cancel, subscribe or renew
                    subscriptions on behalf of users who have completed in-app purchases.
                </p>
                <p>
                    Where users have completed a subscription using a debit or credit card, they can
                    contact LiveLink directly for queries, or manage their subscription via the
                    relevant settings in our product.
                </p>
                <h2>Refunds</h2>
                <h3>Policy</h3>
                <p>
                    Refunds can be issued if a Live is more than 7 days away. If an attendee is
                    unable to make a Live with less than 7 days notice, they will not be eligible
                    for a refund.
                </p>
                <p>
                    If a Live is more than 7 days away, and an attendee would like to request a
                    refund,{" "}
                    <a href="https://forms.gle/9owZefuWymK51dm78" target="_blank" rel="noreferrer">
                        please complete the following form
                    </a>
                    .
                </p>
                <h3>You purchased tickets for a Live that did not occur</h3>
                <p>Make sure you meet all the requirements below:</p>
                <ul>
                    <li>
                        You contacted <a href="mailto:support@livelink.vip">support@livelink.vip</a>
                         and explained your request
                    </li>
                    <li>
                        The event was scheduled to take place within the last 21 days but did not
                        occur (not postponed or rescheduled)
                    </li>
                    <li>The creator used LiveLink’s Payment Processing to process your payment</li>
                </ul>
                <p>
                    If you meet the requirements, ask for a refund through this 
                    <a href="https://forms.gle/9owZefuWymK51dm78" target="_blank" rel="noreferrer">
                        refund request form.
                    </a>
                </p>
            </div>
        </>;
};
export default TermsPage;